import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, tap, map, catchError } from "rxjs";
import { GroupName, GroupUtils } from "../utils/group-names";
import { CalAngularService } from "@cvx/cal-angular";

/**
 * Interface for cached group membership data
 */
interface GroupMembershipCache {
  [groupName: string]: {
    isInGroup: boolean;
    timestamp: number;
    expiresAt: number;
  };
}

/**
 * Service that caches group membership results to avoid redundant API calls
 * and speed up both route filtering and guard checks
 */
@Injectable({
  providedIn: "root",
})
export class GroupMembershipCacheService {
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
  private cache: GroupMembershipCache = {};
  private _cacheUpdated = new BehaviorSubject<void>(void 0);

  public cacheUpdated$ = this._cacheUpdated.asObservable();

  constructor(private calAngularService: CalAngularService) {}

  /**
   * Get group membership status, using cache if available and valid
   * @param groupName - The group to check membership for
   * @param forceRefresh - Force a fresh API call even if cached data exists
   * @returns Observable<boolean> indicating group membership
   */
  public isUserInGroup(
    groupName: GroupName,
    forceRefresh = false
  ): Observable<boolean> {
    // Check cache first (unless forcing refresh)
    if (!forceRefresh && this.isCacheValid(groupName)) {
      const cachedResult = this.cache[groupName].isInGroup;
      return of(cachedResult);
    }

    // Make API call and cache the result
    return this.calAngularService.isInGroup(groupName).pipe(
      tap((isInGroup) => {
        this.setCachedResult(groupName, isInGroup);
      })
    );
  }

  /**
   * Get multiple group memberships efficiently
   * @param groupNames - Array of groups to check
   * @param forceRefresh - Force fresh API calls for all groups
   * @returns Observable<Map<GroupName, boolean>> with membership results
   */
  public getUserGroupMemberships(
    groupNames: GroupName[],
    forceRefresh = false
  ): Observable<Map<GroupName, boolean>> {
    const results = new Map<GroupName, boolean>();
    const groupsToCheck: GroupName[] = [];

    // Check which groups we have valid cache for
    groupNames.forEach((groupName) => {
      if (!forceRefresh && this.isCacheValid(groupName)) {
        results.set(groupName, this.cache[groupName].isInGroup);
      } else {
        groupsToCheck.push(groupName);
      }
    }); // If all groups are cached, return immediately
    if (groupsToCheck.length === 0) {
      return of(results);
    }

    // Check remaining groups via API
    return new Observable<Map<GroupName, boolean>>((observer) => {
      let completedChecks = 0;
      const totalChecks = groupsToCheck.length;

      groupsToCheck.forEach((groupName) => {
        this.calAngularService.isInGroup(groupName).subscribe({
          next: (isInGroup) => {
            this.setCachedResult(groupName, isInGroup);
            results.set(groupName, isInGroup);
            completedChecks++;

            if (completedChecks === totalChecks) {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            console.error(`Error checking group ${groupName}:`, error);
            // On error, assume no access (secure by default)
            this.setCachedResult(groupName, false);
            results.set(groupName, false);
            completedChecks++;

            if (completedChecks === totalChecks) {
              observer.next(results);
              observer.complete();
            }
          },
        });
      });
    });
  }

  /**
   * Get multiple individual group memberships efficiently
   * @param groupNames - Array of individual group names (strings)
   * @param forceRefresh - Force fresh API calls for all groups
   * @returns Observable<Map<string, boolean>> with membership results
   */
  public getIndividualGroupMemberships(
    groupNames: string[],
    forceRefresh = false
  ): Observable<Map<string, boolean>> {
    const results = new Map<string, boolean>();
    const groupsToCheck: string[] = [];

    // Check which groups we have valid cache for
    groupNames.forEach((groupName) => {
      if (!forceRefresh && this.isCacheValid(groupName as GroupName)) {
        results.set(groupName, this.cache[groupName].isInGroup);
      } else {
        groupsToCheck.push(groupName);
      }
    }); // If all groups are cached, return immediately
    if (groupsToCheck.length === 0) {
      return of(results);
    }

    // Check remaining groups via API
    return new Observable<Map<string, boolean>>((observer) => {
      let completedChecks = 0;
      const totalChecks = groupsToCheck.length;

      groupsToCheck.forEach((groupName) => {
        this.calAngularService.isInGroup(groupName).subscribe({
          next: (isInGroup) => {
            this.setCachedResult(groupName as GroupName, isInGroup);
            results.set(groupName, isInGroup);
            completedChecks++;

            if (completedChecks === totalChecks) {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            console.error(
              `Error checking individual group ${groupName}:`,
              error
            );
            // On error, assume no access (secure by default)
            this.setCachedResult(groupName as GroupName, false);
            results.set(groupName, false);
            completedChecks++;

            if (completedChecks === totalChecks) {
              observer.next(results);
              observer.complete();
            }
          },
        });
      });
    });
  }

  /**
   * Check if a specific group membership is cached and valid
   * @param groupName - The group to check
   * @returns boolean indicating if valid cache exists
   */
  public isCacheValid(groupName: GroupName): boolean {
    const cached = this.cache[groupName];
    if (!cached) {
      return false;
    }

    const now = Date.now();
    return now < cached.expiresAt;
  }

  /**
   * Get cached result for a group (returns null if not cached or expired)
   * @param groupName - The group to get cached result for
   * @returns boolean | null
   */
  public getCachedResult(groupName: GroupName): boolean | null {
    if (this.isCacheValid(groupName)) {
      return this.cache[groupName].isInGroup;
    }
    return null;
  }

  /**
   * Set cached result for a group
   * @param groupName - The group name
   * @param isInGroup - Whether user is in the group
   */
  private setCachedResult(groupName: GroupName, isInGroup: boolean): void {
    const now = Date.now();
    this.cache[groupName] = {
      isInGroup,
      timestamp: now,
      expiresAt: now + this.CACHE_DURATION_MS,
    };

    // Notify subscribers that cache was updated
    this._cacheUpdated.next();
  }

  /**
   * Clear all cached results (useful when user logs out or permissions change)
   */ public clearCache(): void {
    this.cache = {};
    this._cacheUpdated.next();
  }

  /**
   * Clear cache for specific groups
   * @param groupNames - Groups to clear from cache
   */ public clearCacheForGroups(groupNames: GroupName[]): void {
    groupNames.forEach((groupName) => {
      delete this.cache[groupName];
    });
    this._cacheUpdated.next();
  }

  /**
   * Get cache statistics for debugging
   */
  public getCacheStats(): {
    totalCached: number;
    validEntries: number;
    expiredEntries: number;
    cacheHitRate?: number;
  } {
    const now = Date.now();
    const entries = Object.values(this.cache);
    const validEntries = entries.filter((entry) => now < entry.expiresAt);

    return {
      totalCached: entries.length,
      validEntries: validEntries.length,
      expiredEntries: entries.length - validEntries.length,
    };
  }

  /**
   * Preload group memberships for better performance
   * @param groupNames - Groups to preload
   */
  public preloadGroupMemberships(groupNames: GroupName[]): Observable<void> {
    return new Observable<void>((observer) => {
      this.getUserGroupMemberships(groupNames).subscribe({
        next: () => {
          observer.next();
          observer.complete();
        },
        error: (error) => {
          console.error("Error preloading group memberships:", error);
          observer.error(error);
        },
      });
    });
  }

  /**
   * Preload individual group memberships for better performance
   * @param groupNames - Individual group names to preload
   */
  public preloadIndividualGroupMemberships(
    groupNames: string[]
  ): Observable<void> {
    return new Observable<void>((observer) => {
      this.getIndividualGroupMemberships(groupNames).subscribe({
        next: () => {
          observer.next();
          observer.complete();
        },
        error: (error) => {
          console.error(
            "Error preloading individual group memberships:",
            error
          );
          observer.error(error);
        },
      });
    });
  }
}
